<template>
  <div>
    <v-dialog
      v-model="dialog"
      max-width="550"
    >
      <v-col class="text-right">
        <v-card>
          <v-card-title class="text-h5 grey lighten-2">
            Bulk Properties Upload
          </v-card-title>

          <v-card-text>
            <br>
            <span>File must be in .csv format. Please <a
              :href="csvPath"
              download
            >download the sample sheet</a></span>
            <br>
            <v-file-input
              v-model="uploadedCSV"
              label="Select Spreadsheet"
              outlined
              dense
              @click:clear="errors = []"
            />
            <v-list
              v-if="errors"
            >
              <v-card-text
                class="red--text ms-4"
                align="left"
                text
              >
                <div
                  v-for="(err, index) in errors"
                  :key="index"
                >
                  {{ err.detail }}
                </div>
              </v-card-text>
            </v-list>
            <v-btn
              class="icon-btn mr-8 mt-2"
              color="secondary"
              width="100px"
              @click="dialog = false"
            >
              cancel
            </v-btn>
            <v-btn
              :loading="loading"
              class="icon-btn mr-8 mt-2"
              color="primary"
              width="180px"
              @click="uploadCSV"
            >
              Upload Properties
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-dialog>
    <div class="mr-8 d-flex justify-space-between">
      <h1 class="ml-8">
        Properties
      </h1>
      <v-btn
        v-if="!isInternal && currentSelectedOrg.id && canManageOrg(currentSelectedOrg.id)"
        class="icon-btn mr-8 mt-2"
        color="primary"
        width="155px"
        @click="handleAddProperty"
      >
        <v-icon
          size="14px"
          class="mr-2"
        >
          $faPlus
        </v-icon>Add Property
      </v-btn>
      <!--The menu should only display for internal user. -->
      <v-menu
        v-if="isInternal && currentSelectedOrg.id && canManageOrg(currentSelectedOrg.id)"
        offset-y
        origin="top right"
        transition="scale-transition"
      >
        <template v-slot:activator="{ on, attrs, value }">
          <v-btn
            color="primary"
            class="filter-btn"
            v-bind="attrs"
            v-on="on"
          >
            <span>Add Property</span>
            <font-awesome-icon :icon="value ? 'chevron-up' : 'chevron-down'" />
          </v-btn>
        </template>
        <v-list dense>
          <v-list-item
            class="account-menu-item"
            @click="handleAddProperty"
          >
            <v-list-item-content class="account-menu-item__content">
              Add Property
            </v-list-item-content>
            <v-list-item-icon class="account-menu-item__icon">
              <v-icon
                size="14px"
                class="mr-2"
              >
                $faPlus
              </v-icon>
            </v-list-item-icon>
          </v-list-item>
          <v-list-item
            class="account-menu-item"
            @click="dialog = true"
          >
            <v-list-item-content class="account-menu-item__content">
              Add Bulk Properties
            </v-list-item-content>
            <v-list-item-icon class="account-menu-item__icon">
              <v-icon
                size="14px"
                class="mr-2"
              >
                $faPlus
              </v-icon>
            </v-list-item-icon>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>
    <v-card class="mx-8 mt-5">
      <v-tabs
        v-model="activeTab"
        class="prop-tabs"
        background-color="transparent"
        height="63px"
      >
        <v-tabs-slider
          color="muted"
          style="max-width: 204px;"
        />
        <valkyrie-tab
          v-for="(tab, i) in tabs"
          :id="`${tab.text}-tab`"
          :key="i"
          :icon="tab.icon"
          :text="tab.total ? `${tab.text} (${tab.total})` : tab.text"
        />
        <v-tabs-items
          v-model="activeTab"
          class="transparent"
        >
          <v-tab-item
            v-for="(tab, i) in tabs"
            :key="i"
            class="tab-window"
          >
            <keep-alive>
              <div style="min-height: 200px">
                <component
                  :is="`${tabs[activeTab].list}List`"
                  v-if="activeTab === i"
                  :filter-by="'organization_id'"
                />
              </div>
            </keep-alive>
          </v-tab-item>
        </v-tabs-items>
      </v-tabs>
    </v-card>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'
  import PropertiesList from '@/components/properties/PropertiesList'
  import PropertyZonesList from '@/components/activityZones/PropertyZonesList'
  import ValkyrieTab from '@/components/base/ValkyrieTab'
  import csvPath from '../../../public/bulk_properties_example.csv'

  export default {
    name: 'PropertyListMainView',
    components: {
      PropertiesList,
      PropertyZonesList,
      ValkyrieTab,
    },
    data: () => ({
      activeTab: 0,
      hasFilters: false,
      dialog: false,
      loading: false,
      errors: [],
      uploadedCSV: undefined,
    }),
    computed: {
      ...mapGetters({
        currentSelectedOrg: 'Permissions/currentSelectedOrg',
        totalProperties: 'Properties/total',
        totalZones: 'PropertyZones/total',
        canAdminOrg: 'Permissions/canAdminOrg',
        canManageOrg: 'Permissions/canManageOrg',
        isInternal: 'Permissions/isInternal',
      }),
      csvPath () {
        return csvPath
      },
      tabs () {
        return [
          {
            list: 'Properties',
            text: 'Properties',
            total: `${this.totalProperties || '...'}`,
            icon: '$faHome',
          },
          {
            list: 'PropertyZones',
            text: 'Sensors',
            total: `${this.totalZones || '...'}`,
            icon: '$faBell',
          },
        ]
      },
    },
    methods: {
      handleAddProperty () {
        this.$router.push('/properties/add_property')
      },
      async uploadCSV () {
        this.loading = true
        const payload = new FormData()
        payload.append('list', this.uploadedCSV)
        if (!this.uploadedCSV) {
          const payload = { type: 'warning', text: 'No File' }
          this.$store.dispatch('updateMessages', payload)
          this.loading = false
          return
        }
        await this.$store.dispatch('Properties/createBulkProperty', payload).then((res) => {
          this.$emit('file-uploaded')
          this.errors = []
        })
          .catch(err => {
            if (err) {
              this.errors = err.response.data.errors
              if (err.response.data.data !== null && err.response.data.data.length > 0) {
                const successPayload = { type: 'success', text: `Successfully created ${err.response.data.data.length} properties` }
                this.$store.dispatch('updateMessages', successPayload, { root: true })
              }
            }
          })
          .finally(() => { this.loading = false })
      },
    },
  }
</script>
<style lang="scss"></style>
